import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { debugTicketSync, migrateTicketsToNewFormat, forceSyncWithAndroid } from '../services/raffleService';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import toast from 'react-hot-toast';

const DebugSyncPage = () => {
  const { raffleId } = useParams();
  const [ticketNumber, setTicketNumber] = useState('');
  const [debugInfo, setDebugInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [raffleStats, setRaffleStats] = useState(null);

  const analyzeRaffle = async () => {
    setLoading(true);
    try {
      const ticketsRef = collection(db, 'raffles', raffleId, 'tickets');
      const snapshot = await getDocs(ticketsRef);
      
      let stats = {
        total: 0,
        reserved: 0,
        sold: 0,
        missingFields: [],
        webReservations: 0,
        androidReservations: 0
      };

      snapshot.forEach(doc => {
        const data = doc.data();
        stats.total++;
        
        if (data.reserved || data.isReserved) stats.reserved++;
        if (data.sold || data.isSold) stats.sold++;
        if (data.source === 'web' || data.platform === 'web') stats.webReservations++;
        if (data.source === 'android' || data.platform === 'android') stats.androidReservations++;
        
        // Check for missing fields
        const missingTicket = {
          number: doc.id,
          missing: []
        };
        
        if ((data.reserved || data.sold) && !data.isReserved && !data.isSold) {
          missingTicket.missing.push('isReserved/isSold');
        }
        if ((data.reserved || data.sold) && !data.status) {
          missingTicket.missing.push('status');
        }
        if ((data.reserved || data.sold) && !data.ticketStatus) {
          missingTicket.missing.push('ticketStatus');
        }
        
        if (missingTicket.missing.length > 0) {
          stats.missingFields.push(missingTicket);
        }
      });

      setRaffleStats(stats);
      toast.success('Análisis completado');
    } catch (error) {
      toast.error('Error al analizar: ' + error.message);
    }
    setLoading(false);
  };

  const debugTicket = async () => {
    if (!ticketNumber) {
      toast.error('Ingrese un número de boleto');
      return;
    }
    
    setLoading(true);
    try {
      await debugTicketSync(raffleId, ticketNumber);
      setDebugInfo('Revisa la consola del navegador para ver los detalles');
      toast.success('Debug completado - revisa la consola');
    } catch (error) {
      toast.error('Error: ' + error.message);
    }
    setLoading(false);
  };

  const migrateTickets = async () => {
    if (!window.confirm('¿Migrar todos los boletos a la nueva estructura?')) return;
    
    setLoading(true);
    try {
      const result = await migrateTicketsToNewFormat(raffleId);
      toast.success(`Migración completada: ${result.migrated} boletos actualizados`);
      await analyzeRaffle(); // Re-analyze after migration
    } catch (error) {
      toast.error('Error en migración: ' + error.message);
    }
    setLoading(false);
  };
  
  const forceSync = async () => {
    setLoading(true);
    try {
      await forceSyncWithAndroid(raffleId);
      toast.success('Señal de sincronización enviada a Android');
    } catch (error) {
      toast.error('Error al forzar sincronización: ' + error.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">Debug Sincronización Android</h1>
        
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Rifa ID: {raffleId}</h2>
          
          <div className="space-y-4">
            <button
              onClick={analyzeRaffle}
              disabled={loading}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-gray-400"
            >
              Analizar Rifa
            </button>
            
            <button
              onClick={forceSync}
              disabled={loading}
              className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 disabled:bg-gray-400 ml-2"
            >
              Forzar Sincronización con Android
            </button>

            {raffleStats && (
              <div className="mt-4 p-4 bg-gray-100 rounded">
                <h3 className="font-semibold mb-2">Estadísticas:</h3>
                <p>Total boletos: {raffleStats.total}</p>
                <p>Reservados: {raffleStats.reserved}</p>
                <p>Vendidos: {raffleStats.sold}</p>
                <p>Reservas desde Web: {raffleStats.webReservations}</p>
                <p>Reservas desde Android: {raffleStats.androidReservations}</p>
                
                {raffleStats.missingFields.length > 0 && (
                  <div className="mt-4">
                    <p className="text-red-600 font-semibold">
                      Boletos con campos faltantes: {raffleStats.missingFields.length}
                    </p>
                    <button
                      onClick={migrateTickets}
                      disabled={loading}
                      className="mt-2 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:bg-gray-400"
                    >
                      Migrar Boletos
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Debug Boleto Individual</h2>
          
          <div className="flex gap-2">
            <input
              type="text"
              value={ticketNumber}
              onChange={(e) => setTicketNumber(e.target.value)}
              placeholder="Número de boleto"
              className="flex-1 px-3 py-2 border rounded"
            />
            <button
              onClick={debugTicket}
              disabled={loading}
              className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 disabled:bg-gray-400"
            >
              Debug Boleto
            </button>
          </div>
          
          {debugInfo && (
            <p className="mt-4 text-gray-600">{debugInfo}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DebugSyncPage;